import { useMemo } from 'react';
import { useDropzone, type DropzoneOptions, ErrorCode } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import _uniqBy from 'lodash/uniqBy';
import { Button, CircularProgress, Stack } from '@mui/material';

import {
  useNotification,
  NotificationProvider,
} from 'shared/utils/Notification';

import { EntityType } from '../../model';
import { ClientFilesList, MixedFilesList } from '../FilesList';

import { Styled } from './Dropzone.styled';

type ValidateFn = NonNullable<DropzoneOptions['validator']>;

const validateByFileSize: ValidateFn = (file) => {
  if (file.size > 0) return null;

  return {
    code: ErrorCode.FileTooSmall,
    message: `Tiedosto (${file.name}) on tyhjä eikä sitä voida ladata.`,
  };
};

const validateUntilFirstError = (
  file: File,
  validateFns: ValidateFn[]
): ReturnType<ValidateFn> => {
  for (const validateFn of validateFns) {
    const validateFnRes = validateFn(file);

    if (validateFnRes) return validateFnRes;
  }

  return null;
};

const defaultValidateFns: ValidateFn[] = [validateByFileSize];

export type DropzoneProps = {
  disabled?: boolean;
  value?: File[];
  validateFns?:
    | ValidateFn[]
    | ((defaultValidateFns: ValidateFn[]) => ValidateFn[]);
  isLoading?: boolean;
  onChange: (files: File[]) => void;

  documentsToBeDeleted?: string[];
  entityId?: string | null;
  entityType?: EntityType;
  onAddDocumentToDeletionQueue?: (documentId: string) => void;
};

const Dropzone = ({
  disabled,
  isLoading = false,
  value: files,
  validateFns: validateFnsProp,
  onChange,

  documentsToBeDeleted,
  entityId,
  entityType,
  onAddDocumentToDeletionQueue,
}: DropzoneProps) => {
  const { t } = useTranslation();

  const validateFns = useMemo(() => {
    if (typeof validateFnsProp === 'function') {
      return validateFnsProp(defaultValidateFns);
    }

    if (Array.isArray(validateFnsProp)) return validateFnsProp;

    return defaultValidateFns;
  }, [validateFnsProp]);

  const notification = useNotification();

  const handleChangeFiles = (acceptedFiles: File[]) => {
    if (typeof onChange === 'function') {
      const newFilesList = files
        ? _uniqBy([...files, ...acceptedFiles], 'name')
        : acceptedFiles;

      onChange(newFilesList);
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    disabled,
    noClick: true,
    noKeyboard: true,
    onDropAccepted: handleChangeFiles,
    validator: (file) => {
      const validationError = validateUntilFirstError(file, validateFns);
      if (!validationError) return null;

      const firstValidationError = Array.isArray(validationError)
        ? validationError[0]
        : validationError;

      notification.open({
        description: firstValidationError.message,
        type: 'warning',
      });

      return firstValidationError;
    },
  });

  const showMixedFilesList = useMemo(() => {
    if (!Array.isArray(documentsToBeDeleted)) return false;
    if (!entityId) return false;
    if (typeof entityType !== 'number') return false;
    if (typeof onAddDocumentToDeletionQueue !== 'function') return false;

    return true;
  }, [
    documentsToBeDeleted,
    entityId,
    entityType,
    onAddDocumentToDeletionQueue,
  ]);

  return (
    <Styled.Container
      alignItems="center"
      direction="column"
      justifyContent="center"
      spacing={1.5}
      sx={{ ...(disabled && { opacity: 0.3 }) }}
    >
      <Styled.Wrapper>
        {isLoading ? (
          <CircularProgress color="primary" size={30} />
        ) : (
          <Stack
            {...getRootProps()}
            alignItems="center"
            direction="row"
            justifyContent="center"
            spacing={1.5}
          >
            <input {...getInputProps()} />
            <Styled.Label>{t('DocDrop')}</Styled.Label>
            <Styled.Label>{t('Or')}</Styled.Label>
            <Button
              disabled={disabled}
              color="secondary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
              onClick={open}
            >
              {t('Browse')}
            </Button>
          </Stack>
        )}
      </Styled.Wrapper>

      <Stack width="100%" spacing={0.75}>
        {showMixedFilesList && (
          <MixedFilesList
            documentsToBeDeleted={documentsToBeDeleted!}
            entityId={entityId!}
            entityType={entityType!}
            onAddDocumentToDeletionQueue={onAddDocumentToDeletionQueue!}
          />
        )}
        {files && !!files.length && (
          <ClientFilesList files={files} onChange={onChange} />
        )}
      </Stack>
    </Styled.Container>
  );
};

const DropzoneWithNotificationProvider = (props: DropzoneProps) => (
  <NotificationProvider>
    <Dropzone {...props} />
  </NotificationProvider>
);

export default DropzoneWithNotificationProvider;
